<template>
  <div class="box box--default product">
    <h4 v-if="title" class="mrgt0 mrgb-">
      {{ title }}
    </h4>

    <div
      v-if="product.label"
      class="media media--middle media--small product__title mrgb"
    >
      <div class="media__body">
        {{ product.label }} <small>{{ gettext('de biogaz') }}</small>
      </div>
      <div class="media_right">
        <Icon :id="product.bioIcon" class="icon--150 sm-icon--200" />
      </div>
    </div>

    <div class="product__price text-huge mrgb--">
      <span class="text-brand text-bold">
        {{ cost }}
      </span>
      <span class="text-muted">
        <small>
          <template v-if="product.type === 'product'">{{
            gettext('par an')
          }}</template
          >¹
        </small>
      </span>
    </div>

    <div>
      <template v-if="usageId === 'mobility'">
        {{
          gettext(
            'Ce montant correspond à la plus-value écologique pour rouler 100% au biogaz. Il s’ajoute au montant estimatif de votre plein.'
          )
        }}
      </template>
      <template v-else-if="shouldIncludePower && simulation.power">
        {{
          interpolate(
            gettext(
              'Sur la base d’une consommation de %s kWh et d’une puissance d’installation de %s KW.'
            ),
            [
              formatNumber(simulation.consumption),
              formatNumber(simulation.power),
            ]
          )
        }}
      </template>
      <template v-else>
        {{
          interpolate(gettext('Sur la base d’une consommation de %s kWh.'), [
            formatNumber(simulation.consumption),
          ])
        }}
      </template>
      <template v-if="usageId !== 'mobility' && product.percentage < 100">
        {{
          interpolate(
            gettext(
              'L’énergie fournie comprend une part d’énergie renouvelable : %s de biogaz européen.'
            ),
            [product.label]
          )
        }}
      </template>
    </div>

    <div class="box__footer">
      <div class="flex flex-column sm-flex-row sm-align-items-center">
        <div class="flex-fill flex align-items-center text-muted text-small">
          <template v-if="usageId === 'mobility'">
            {{
              interpolate(
                gettext(
                  'Le GNC contenant d’office 20% de biogaz, seule une part de %s% de biogaz doit être achetée. Dans le cas présent, cela représente %s kg de biogaz.'
                ),
                [
                  formatNumber(mobilityPercentage),
                  formatNumber(mobilityQuantity),
                ]
              )
            }}
          </template>
          <template v-else-if="rateGroupLabel">
            <div class="mrgr-">
              {{ gettext('Tarif applicable :') }} {{ rateGroupLabel }}
            </div>
            <img
              v-if="rate"
              :src="rate.image"
              :height="rate.height"
              alt=""
              class="inline-block"
            />
          </template>
        </div>

        <div class="mrgt sm-mrgt0 sm-mrgl">
          <button
            type="button"
            class="btn btn--primary btn--block"
            @click="order(props.product)"
          >
            {{ gettext('Sélectionner') }}
            <Icon id="chevron-right" class="icon--75 mrgl--" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

import {
  GNC_BIOGAZ_PRICE_PER_KG,
  GNC_BIOGAZ_PERCENT_INCLUDED,
} from '@/config/simulation';
import { addVAT, formatCurrency, formatNumber } from '@/helpers/utils';

import { useSimulation } from '@/composables/useSimulation';
import planBasic10 from '@/../images/plan-basic-10.svg';
import planCH100 from '@/../images/plan-ch-100.svg';

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  title: {
    type: String,
    default: null,
  },
});

const {
  costs,
  usageId,
  simulation,
  rateGroup,
  mobilityTotal,
  order,
  shouldIncludePower,
} = useSimulation();

const mobilityPercentage = computed(() => {
  return props.product.percentage - GNC_BIOGAZ_PERCENT_INCLUDED;
});

const mobilityQuantity = computed(() => {
  return mobilityTotal.value * (mobilityPercentage.value / 100);
});

const rateGroupLabel = computed(() => {
  const groups = {
    comfort: gettext('Confort'),
    cooking: gettext('Cuisson'),
    other: gettext('Autres usages'),
  };
  return groups[rateGroup.value];
});

const rate = computed(() => {
  if (props.product.percentage === 100) {
    return { image: planCH100, height: 22 };
  }
  if (props.product.percentage === 0) {
    return { image: planBasic10, height: 19 };
  }
  return null;
});

const cost = computed(() => {
  if (usageId.value === 'mobility') {
    return formatCurrency(
      addVAT(mobilityQuantity.value * GNC_BIOGAZ_PRICE_PER_KG)
    );
  } else {
    return costs.value.prices[props.product.percentage];
  }
});
</script>

<style lang="scss" scoped>
.product {
  position: relative;
}

.product__title {
  padding: $spacing-unit-small $spacing-unit-default;
  margin-left: -$spacing-unit-default;
  margin-right: -$spacing-unit-default;

  color: #69b42e;
  font-size: rem(36px);
  font-weight: 400;
  letter-spacing: -0.1rem;
  line-height: 1;

  background-color: $success-100;

  @include media('sm') {
    font-size: rem(44px);
  }

  small {
    font-size: 45%;
    letter-spacing: normal;
  }
}

.product__price {
  line-height: 1.2;
}
</style>
